import type { CashbackStatusResponse } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

interface UseCashbackStatusOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch the authenticated user's cashback status
 * Returns accumulated cashback, claimable amount, monthly usage, and unlock progress
 */
export const useCashbackStatus = (options?: UseCashbackStatusOptions) => {
  const { hubUser, auth } = useHubUser();
  const apiHelper = useApi();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [QueryKeys.CASHBACK_STATUS, hubUser?.id],
    queryFn: async (): Promise<CashbackStatusResponse> => {
      return apiHelper.getWithAuth(auth, API_ENDPOINT.CASHBACK_STATUS);
    },
    enabled: (options?.enabled ?? true) && !!hubUser?.id,
    staleTime: 30 * 1000, // 30 seconds
    retry: 2,
  });

  return {
    status: data ?? null,
    accumulated: data?.accumulated ?? null,
    claimable: data?.claimable ?? null,
    monthlyUsage: data?.monthlyUsage ?? null,
    unlockProgress: data?.unlockProgress ?? null,
    isLoading,
    error: error?.message ?? null,
    refetch,
  };
};
